/**
 * Utility functions for managing player stat progression
 * 
 * === BALANCED STAT PROGRESSION SYSTEM ===
 * 
 * This system provides a balanced and rewarding progression path where:
 * 
 * 1. **BASE STATS**: All stats start at 10 (Str, Dex, Int, Con, Luck, Def)
 * 
 * 2. **SKILL PROGRESSION**: Each skill provides +1 stat per 10 levels
 *    - At level 100, each skill provides +10 total stats
 *    - Skills focus on 1-2 related stats for clear progression paths
 *    - Example: Mining gives +7 Strength, +3 Constitution per 10 levels
 * 
 * 3. **ACHIEVEMENT REWARDS**: Meaningful bonuses for major milestones
 *    - Early achievements: +1-3 stats
 *    - Major achievements: +5-10 stats
 *    - Legendary achievements: +10 to all stats
 * 
 * 4. **RESOURCE SCALING**: Health/Mana/Energy now scale significantly
 *    - Health: 100 + (Constitution × 10) - Can reach 1000+ with progression
 *    - Mana: 75 + (Intelligence × 8) - Can reach 800+ with progression  
 *    - Energy: 100 + (Constitution × 5) - Can reach 500+ with progression
 * 
 * 5. **BALANCED TARGETS**: With dedication, players can reach:
 *    - 100+ in all stats through skills and achievements
 *    - 1000+ Health, 800+ Mana, 500+ Energy
 *    - Equipment provides additional bonuses beyond 100
 * 
 * 6. **PROGRESSION PATHS**:
 *    - Combat: Strength, Constitution, Defense
 *    - Gathering: Strength, Dexterity, Constitution
 *    - Crafting: Intelligence, Dexterity
 *    - Magic: Intelligence, Luck
 *    - Survival: Constitution, Defense
 */

/**
 * Base stat values that all characters start with
 */
export const BASE_STATS = {
    strength: 10,
    dexterity: 10,
    intelligence: 10,
    constitution: 10,
    luck: 0,
    defense: 0
};

/**
 * Skill-to-stat mapping for milestone bonuses
 * Every 10 levels in a skill provides the specified bonus
 * At level 100, each skill provides +10 total to related stats
 */
export const SKILL_STAT_BONUSES = {
    // Core skills - Each skill focuses on 1-2 stats to reach 100
    mining: { strength: 0.7, constitution: 0.3 },
    woodcutting: { strength: 0.7, constitution: 0.3 },
    fishing: { dexterity: 0.7, luck: 0.3 },
    farming: { constitution: 0.7, intelligence: 0.3 },
    foraging: { luck: 0.7, intelligence: 0.3 },
    
    // Combat skills - Focused progression
    combat: { strength: 0.4, constitution: 0.3, defense: 0.3 },
    attack: { strength: 1.0 },
    magic: { intelligence: 1.0 },
    archery: { dexterity: 1.0 },
    
    // Secondary skills - Balanced progression
    refining: { intelligence: 0.7, constitution: 0.3 },
    crafting: { intelligence: 0.6, dexterity: 0.4 },
    fletching: { dexterity: 0.7, intelligence: 0.3 },
    alchemy: { intelligence: 0.8, luck: 0.2 },
    cooking: { intelligence: 0.6, constitution: 0.4 },
    herblore: { intelligence: 0.7, luck: 0.3 },
    smithing: { strength: 0.6, intelligence: 0.4 },
    harvesting: { dexterity: 0.5, luck: 0.5 },
    gambling: { luck: 1.0 },
    stealing: { dexterity: 0.7, luck: 0.3 },
    
    // Utility skills - Balanced progression
    commerce: { intelligence: 0.6, luck: 0.4 },
    logistics: { strength: 0.5, constitution: 0.5 },
    construction: { strength: 0.6, intelligence: 0.4 },
    exploration: { constitution: 0.5, luck: 0.5 },
    survival: { constitution: 0.7, defense: 0.3 }
};

/**
 * Achievement-based stat rewards
 * These provide smaller bonuses to supplement skill progression
 */
export const ACHIEVEMENT_STAT_REWARDS = {
    // Combat achievements - Monster Hunter series
    'monsterHunter1': { strength: 2 },
    'monsterHunter2': { strength: 2, constitution: 2 },
    'monsterHunter3': { strength: 3, constitution: 3 },
    'monsterHunter4': { strength: 5, constitution: 5, defense: 2 },
    
    // Combat achievements - Slime Slayer series
    'slimeSlayer1': { strength: 1 },
    'slimeSlayer2': { strength: 2 },
    'slimeSlayer3': { strength: 3, constitution: 2 },
    'slimeSlayer4': { strength: 5, constitution: 3 },
    
    // Combat achievements - Goblin Bane series
    'goblinBane1': { strength: 1 },
    'goblinBane2': { strength: 2, dexterity: 1 },
    'goblinBane3': { strength: 3, dexterity: 2 },
    
    // Combat achievements - Combat streak
    'combatStreak1': { strength: 2 },
    'combatStreak2': { strength: 2, constitution: 2, defense: 2 },
    'combatStreak3': { strength: 3, constitution: 3, defense: 3 },
    
    // Combat achievements - Damage
    'bigHitter1': { strength: 2 },
    'bigHitter2': { strength: 3 },
    'totalDamage1': { strength: 2, constitution: 2 },
    'totalDamage2': { strength: 3, constitution: 3 },
    
    // Economic achievements
    'goldCollector1': { luck: 2 },
    'goldCollector2': { luck: 2, intelligence: 2 },
    'goldCollector3': { luck: 3, intelligence: 2 },
    'goldCollector4': { luck: 5, intelligence: 3 },
    
    // Building achievements
    'firstBuilding': { intelligence: 2 },
    'allBuildings': { intelligence: 3, constitution: 3 },
    'firstUpgrade': { intelligence: 2 },
    'maxUpgrades': { intelligence: 3, constitution: 2 },
    
    // Gathering achievements
    'diligentMiner1': { strength: 1 },
    'diligentMiner2': { strength: 2 },
    'diligentMiner3': { strength: 3, constitution: 2 },
    'masterMiner1': { strength: 2 },
    'masterMiner2': { strength: 2, constitution: 2 },
    'masterMiner3': { strength: 3, constitution: 2 },
    'woodcutterApprentice': { strength: 1 },
    'woodcutterJourneyman': { strength: 2 },
    'woodcutterMaster': { strength: 2, constitution: 2 },
    'woodcutterLegend': { strength: 3, constitution: 3 },
    'fishingBeginner': { dexterity: 1 },
    'fishingEnthusiast': { dexterity: 2 },
    'masterAngler': { dexterity: 2, luck: 2 },
    'legendaryAngler': { dexterity: 3, luck: 3 },
    'foragingNovice': { luck: 1, intelligence: 1 },
    'foragingAdept': { luck: 2, intelligence: 1 },
    'foragingMaster': { luck: 2, intelligence: 2 },
    
    // Crafting achievements
    'firstCraft': { intelligence: 1 },
    'craftingApprentice': { intelligence: 1, dexterity: 1 },
    'craftingJourneyman': { intelligence: 2, dexterity: 2 },
    'masterCrafter': { intelligence: 2, dexterity: 2 },
    'firstRefine': { intelligence: 1 },
    'refiningExpert': { intelligence: 2 },
    'masterRefiner': { intelligence: 2, constitution: 2 },
    'weaponmaster': { strength: 2, intelligence: 2 },
    'armorSmith': { constitution: 2, defense: 2 },
    
    // Skill progression achievements
    'skillLevelUp1': { strength: 1, dexterity: 1, intelligence: 1, constitution: 1, luck: 1, defense: 1 },
    'skillLevelUp2': { strength: 2, dexterity: 2, intelligence: 2, constitution: 2, luck: 2, defense: 2 },
    'skillLevelUp3': { strength: 3, dexterity: 3, intelligence: 3, constitution: 3, luck: 3, defense: 3 },
    'skillLevelUp4': { strength: 5, dexterity: 5, intelligence: 5, constitution: 5, luck: 5, defense: 5 },
    'skillLevelUp5': { strength: 10, dexterity: 10, intelligence: 10, constitution: 10, luck: 10, defense: 10 },
    
    // Special milestone achievements
    'allSkillsLevel10': { strength: 2, dexterity: 2, intelligence: 2, constitution: 2, luck: 2, defense: 2 },
    'allSkillsLevel25': { strength: 3, dexterity: 3, intelligence: 3, constitution: 3, luck: 3, defense: 3 },
    
    // Minigame achievements
    'perfectMining': { strength: 2 },
    'perfectFishing': { dexterity: 2 },
    'perfectForaging': { luck: 2 },
    'perfectWoodchopping': { strength: 2 },
    
    // Special achievements
    'energyEfficient': { constitution: 2 },
    'marathonRunner': { constitution: 3 },
    'speedRunner1': { dexterity: 1 },
    'speedRunner2': { dexterity: 2 },
    'speedRunner3': { dexterity: 3 },
    'survivor': { constitution: 3, defense: 2 }
};

/**
 * Calculate stat bonuses from skill levels
 * @param {Object} skills - Player's skills object
 * @returns {Object} Stat bonuses from skills
 */
export const calculateSkillStatBonuses = (skills) => {
    const bonuses = {
        strength: 0,
        dexterity: 0,
        intelligence: 0,
        constitution: 0,
        luck: 0,
        defense: 0
    };
    
    if (!skills) return bonuses;
    
    Object.entries(skills).forEach(([skillId, skill]) => {
        const skillLevel = skill.level || 1;
        const skillBonuses = SKILL_STAT_BONUSES[skillId];
        
        if (skillBonuses) {
            // Every 10 levels provides the bonus
            const multiplier = Math.floor(skillLevel / 10);
            
            Object.entries(skillBonuses).forEach(([stat, bonus]) => {
                if (bonuses.hasOwnProperty(stat)) {
                    bonuses[stat] += bonus * multiplier;
                }
            });
        }
    });
    
    return bonuses;
};

/**
 * Calculate stat bonuses from completed achievements
 * @param {Array} completedAchievements - Array of completed achievement IDs
 * @returns {Object} Stat bonuses from achievements
 */
export const calculateAchievementStatBonuses = (completedAchievements) => {
    const bonuses = {
        strength: 0,
        dexterity: 0,
        intelligence: 0,
        constitution: 0,
        luck: 0,
        defense: 0
    };
    
    if (!completedAchievements) return bonuses;
    
    completedAchievements.forEach(achievementId => {
        const achievementBonuses = ACHIEVEMENT_STAT_REWARDS[achievementId];
        if (achievementBonuses) {
            Object.entries(achievementBonuses).forEach(([stat, bonus]) => {
                if (bonuses.hasOwnProperty(stat)) {
                    bonuses[stat] += bonus;
                }
            });
        }
    });
    
    return bonuses;
};

/**
 * Calculate stat bonuses from equipped items
 * @param {Object} equipment - Player's equipment object
 * @param {Object} ItemData - Item data definitions
 * @returns {Object} Stat bonuses from equipment
 */
export const calculateEquipmentStatBonuses = (equipment, ItemData) => {
    const bonuses = {
        strength: 0,
        dexterity: 0,
        intelligence: 0,
        constitution: 0,
        luck: 0,
        defense: 0
    };
    
    if (!equipment || !ItemData) return bonuses;
    
    Object.values(equipment).forEach(equippedItem => {
        if (equippedItem && equippedItem.itemKey) {
            const itemData = ItemData[equippedItem.itemKey];
            if (itemData && itemData.bonuses) {
                Object.entries(itemData.bonuses).forEach(([stat, bonus]) => {
                    if (bonuses.hasOwnProperty(stat)) {
                        bonuses[stat] += bonus;
                    }
                });
            }
        }
    });
    
    return bonuses;
};

/**
 * Calculate total player stats including all bonuses
 * @param {Object} playerData - Complete player data object
 * @param {Object} ItemData - Item data definitions
 * @returns {Object} Final calculated stats
 */
export const calculateTotalPlayerStats = (playerData, ItemData) => {
    const baseStats = { ...BASE_STATS };
    const skillBonuses = calculateSkillStatBonuses(playerData.skills);
    const achievementBonuses = calculateAchievementStatBonuses(playerData.completedAchievements);
    const equipmentBonuses = calculateEquipmentStatBonuses(playerData.equipment, ItemData);
    
    const totalStats = {};
    
    Object.keys(baseStats).forEach(stat => {
        totalStats[stat] = Math.floor(
            baseStats[stat] + 
            skillBonuses[stat] + 
            achievementBonuses[stat] + 
            equipmentBonuses[stat]
        );
    });
    
    // Calculate derived stats with improved scaling
    // Base values account for starting stats of 10 in constitution/intelligence
    totalStats.health = {
        current: playerData.health?.current || 100,
        max: (totalStats.constitution * 10) // Constitution provides 10 health per point
    };
    
    totalStats.mana = {
        current: playerData.mana?.current || 75,
        max: (totalStats.intelligence * 8) - 5 // Intelligence provides 8 mana per point, minus 5 for balance
    };
    
    totalStats.energy = {
        current: playerData.energy?.current || 100,
        max: (totalStats.constitution * 5) + 50 // Constitution provides 5 energy per point, plus 50 base
    };
    
    // Combat stats - True unarmed combat with minimal strength bonus
    totalStats.damageMin = 1 + Math.floor((totalStats.strength - 10) * 0.1);
    totalStats.damageMax = 2 + Math.floor((totalStats.strength - 10) * 0.2);
    
    return totalStats;
};

/**
 * Get breakdown of stat sources for UI display
 * @param {Object} playerData - Complete player data object
 * @param {Object} ItemData - Item data definitions
 * @returns {Object} Detailed breakdown of stat sources
 */
export const getStatBreakdown = (playerData, ItemData) => {
    const baseStats = { ...BASE_STATS };
    const skillBonuses = calculateSkillStatBonuses(playerData.skills);
    const achievementBonuses = calculateAchievementStatBonuses(playerData.completedAchievements);
    const equipmentBonuses = calculateEquipmentStatBonuses(playerData.equipment, ItemData);
    
    return {
        base: baseStats,
        skills: skillBonuses,
        achievements: achievementBonuses,
        equipment: equipmentBonuses
    };
}; 